import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class SessionStorageService {
  /**
   * Get an item from sessionStorage and parse it as JSON
   * @param key The storage key
   * @param defaultValue Default value if key doesn't exist or parsing fails
   * @returns Parsed value or defaultValue
   */
  getItem<T>(key: string, defaultValue: T | null = null): T | null {
    if (typeof window === 'undefined' || !sessionStorage) {
      return defaultValue;
    }

    try {
      const item = sessionStorage.getItem(key);
      if (item === null) {
        return defaultValue;
      }
      return JSON.parse(item) as T;
    } catch {
      return defaultValue;
    }
  }

  /**
   * Get a string item from sessionStorage without JSON parsing
   * @param key The storage key
   * @param defaultValue Default value if key doesn't exist
   * @returns String value or defaultValue
   */
  getStringItem(key: string, defaultValue: string | null = null): string | null {
    if (typeof window === 'undefined' || !sessionStorage) {
      return defaultValue;
    }

    return sessionStorage.getItem(key) ?? defaultValue;
  }

  /**
   * Set an item in sessionStorage (automatically stringifies if not a string)
   * @param key The storage key
   * @param value The value to store
   */
  setItem<T>(key: string, value: T): void {
    if (typeof window === 'undefined' || !sessionStorage) {
      return;
    }

    try {
      if (typeof value === 'string') {
        sessionStorage.setItem(key, value);
      } else {
        sessionStorage.setItem(key, JSON.stringify(value));
      }
    } catch (error) {
      console.error(`Error setting sessionStorage item "${key}":`, error);
    }
  }

  /**
   * Remove an item from sessionStorage
   * @param key The storage key
   */
  removeItem(key: string): void {
    if (typeof window === 'undefined' || !sessionStorage) {
      return;
    }

    sessionStorage.removeItem(key);
  }

  /**
   * Clear all items from sessionStorage
   */
  clear(): void {
    if (typeof window === 'undefined' || !sessionStorage) {
      return;
    }

    sessionStorage.clear();
  }

  /**
   * Get userDetails from sessionStorage
   * @returns User details object or empty object
   */
  getUserDetails(): { value?: string | number; [key: string]: any } {
    return this.getItem('userDetails', {}) ?? {};
  }

  /**
   * Get programId from sessionStorage
   * @returns Program ID or null
   */
  getProgramId(): number | null {
    return this.getItem<number>('programId', null);
  }

  /**
   * Get staffInfo from sessionStorage
   * @returns Staff info object or null
   */
  getStaffInfo(): any {
    return this.getItem('staffInfo', null);
  }

  /**
   * Get token from sessionStorage
   * @returns Token string or null
   */
  getToken(): string | null {
    return this.getStringItem('token', null);
  }

  /**
   * Get selected location from sessionStorage
   * @returns Location object { id, locationName, addressLine } or null
   */
  getSelectedLocation(): { id: string; locationName: string; addressLine: string } | null {
    return this.getItem<{ id: string; locationName: string; addressLine: string }>('selectedLocation', null);
  }

  /**
   * Save selected location to sessionStorage
   */
  setSelectedLocation(location: { id: string; locationName: string; addressLine: string }): void {
    this.setItem('selectedLocation', location);
  }
}



